// ============================================================
// Organigrama.jsx — Organigrama con React Flow conectado a datos
// reales del backend (negocios / departamentos+DepartamentoEmpresa
// con dependencia_id / mando_intermedio_de_1 / asistente_de + asesorías
// + personas con cargos).
//
// Construimos el grafo desde las FK del modelo:
//   - dependencia_id   → arista SÓLIDA (línea de mando del árbol).
//   - mando_intermedio → nodo colgado al LADO del padre con arista lateral
//                        (sin entrar al layout dagre, evita empujar el árbol).
//   - asistente_de     → patrón "junction" del spike INNOVA: nodo invisible
//                        sobre el tronco del padre y stub SÓLIDO al asistente.
//   - asesorías        → aristas PUNTEADAS asesor → asesorado (no tocan el
//                        layout, solo dibujan).
// Raíces (las tres FK NULL) cuelgan de un nodo sintético "biz-root" con el
// nombre del negocio para que el grafo se vea SIEMPRE como un único árbol
// (esto es lo que faltaba y por eso los nodos salían desconectados).
//
// Click en una FILA de persona (no en el nodo) abre el modal de detalle.
// Por usabilidad el card lleva la clase 'nodrag' en las filas, para que
// hacer click en una persona no arrastre el nodo de React Flow.
// ============================================================

import React, {
    useContext, useEffect, useMemo, useState, useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ReactFlow, ReactFlowProvider, useReactFlow,
    Background, Controls, MiniMap, Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControl, IconButton, MenuItem, Select, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { fetchEstadosData } from '../../peticiones_API/PeticionesGestionPersonasPersonas';
import {
    fetchAsesoriasOrganigrama,
    fetchDepartamentosFiltersData,
    fetchNegociosData,
    fetchPersonasFiltersData,
    fetchPosicionesOrganigrama,
    savePosicionesOrganigrama,
    resetPosicionesOrganigrama,
} from '../../peticiones_API/PeticionesOrganigrama';
import { UsuarioContext } from '../providers/personaContext';
import { ChevronsDown, Pencil, Save, RotateCcw, X as XCloseIcon } from 'lucide-react';
import GorraCumpleanos from '../img/gorra_cumpleaños.png';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Constantes visuales ───
const BRAND = '#003c66';
const SLATE_200 = '#e2e8f0';
const SLATE_300 = '#cbd5e1';
const SLATE_500 = '#64748b';
const INK = '#0f172a';
const AMBER = '#f39c12';

const NODE_WIDTH = 280;
const NODE_WIDTH_WIDE = 420;
const NODE_HEADER_H = 36;
const NODE_PADDING_V = 8;
const ROW_H = 44;
const MAX_ROWS_BEFORE_SCROLL = 8;
const JUNCTION_SIZE = 2;

// ─── Helpers de persona ───
function nombrePersona(p) {
    return `${p?.nombre || ''} ${p?.apellidos || ''}`.trim() || 'Sin nombre';
}
function siniciales(p) {
    const a = (p?.nombre || '').trim();
    const b = (p?.apellidos || '').trim();
    return ((a[0] || '') + (b[0] || '')).toUpperCase() || '?';
}
// Compara día y mes, ignorando el año.
function esCumpleanos(fechaNacimiento) {
    if (!fechaNacimiento) return false;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    return (
        hoy.getDate() === nacimiento.getDate() &&
        hoy.getMonth() === nacimiento.getMonth()
    );
}
// El endpoint del organigrama oculta `fecha_nacimiento` por privacidad y
// expone un flag derivado `es_cumpleanos_hoy`. Aceptamos ambos para no
// romper si otra fuente envía la fecha completa.
function cumpleHoy(persona) {
    if (persona?.es_cumpleanos_hoy === true) return true;
    return esCumpleanos(persona?.fecha_nacimiento);
}
function hashHue(str) {
    let h = 0;
    const s = String(str || '');
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
}
function avatarColor(p) {
    const hue = hashHue(nombrePersona(p));
    return { bg: `hsl(${hue} 50% 88%)`, fg: `hsl(${hue} 55% 30%)` };
}
function cargoDe(persona, departamentoId) {
    if (!persona || !Array.isArray(persona.Cargos)) return null;
    return persona.Cargos.find((c) => c.departamento_id === departamentoId) || null;
}
function personasDe(personas, departamentoId) {
    const arr = Array.isArray(personas) ? personas : [];
    return arr.filter((p) =>
        Array.isArray(p.Cargos) &&
        p.Cargos.some((c) => c.departamento_id === departamentoId)
    );
}

// ─── Avatar con foto real (fallback a iniciales por hash) ───
// Si la persona cumple años hoy, se superpone el gorrito como overlay.
function Avatar({ persona, size = 32 }) {
    const [err, setErr] = useState(false);
    const tieneFoto = !!(persona && persona.foto && API_URL);
    const showImg = tieneFoto && !err;
    const { bg, fg } = avatarColor(persona);
    const cumple = cumpleHoy(persona);
    const gorroSize = Math.round(size * 0.55);
    return (
        <div
            style={{
                position: 'relative',
                width: size, height: size, flexShrink: 0,
            }}
        >
            <div
                style={{
                    width: size, height: size, borderRadius: '50%',
                    background: bg, color: fg,
                    overflow: 'hidden', display: 'grid', placeItems: 'center',
                    fontSize: size * 0.4, fontWeight: 600, lineHeight: 1,
                    userSelect: 'none',
                }}
            >
                {showImg ? (
                    <img
                        src={`${API_URL}/api/persona/imagen/${persona.foto}`}
                        alt={nombrePersona(persona)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setErr(true)}
                    />
                ) : (
                    <span>{siniciales(persona)}</span>
                )}
            </div>
            {cumple && (
                <img
                    src={GorraCumpleanos}
                    alt=""
                    aria-hidden
                    style={{
                        position: 'absolute',
                        top: -Math.round(gorroSize * 0.45),
                        right: -Math.round(gorroSize * 0.3),
                        width: gorroSize, height: gorroSize,
                        transform: 'rotate(25deg)',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            )}
        </div>
    );
}

// ─── PersonRow: una fila clicable dentro del card ───
// `compact` reduce avatar y tipografía — se usa para los adjuntos pegados
// a su responsable en horizontal, donde el espacio es algo más estrecho.
// `destacado` señala a la cabeza del departamento (responsable): añade un
// chip "Responsable" junto al nombre, sube el peso y un fondo sutil.
function PersonRow({ persona, cargo, departamentoId, onOpen, compact = false, destacado = false }) {
    const avatarSize = compact ? 26 : 32;
    const bgBase = destacado ? 'rgba(0,60,102,0.06)' : 'transparent';
    return (
        <div
            className="nodrag"
            onClick={(e) => {
                e.stopPropagation();
                if (onOpen) onOpen(persona, departamentoId);
            }}
            style={{
                display: 'flex', alignItems: 'center', gap: compact ? 6 : 8,
                padding: 4, borderRadius: 6, cursor: 'pointer',
                minHeight: compact ? 32 : ROW_H - 8, minWidth: 0,
                background: bgBase,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = bgBase; }}
        >
            <Avatar persona={persona} size={avatarSize} />
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
                }}>
                    <div style={{
                        fontSize: compact ? 11 : 12,
                        fontWeight: destacado ? 700 : 600,
                        color: INK,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        lineHeight: 1.2, flex: '0 1 auto', minWidth: 0,
                    }}>
                        {nombrePersona(persona)}
                    </div>
                    {destacado && !compact && (
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                            padding: '1px 6px', borderRadius: 10,
                            background: BRAND, color: '#fff',
                            flexShrink: 0, lineHeight: 1.4,
                        }}>
                            RESPONSABLE
                        </span>
                    )}
                </div>
                <div style={{
                    fontSize: compact ? 10 : 11, color: SLATE_500,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    lineHeight: 1.2,
                }}>
                    {cargo || 'Sin cargo'}
                </div>
            </div>
        </div>
    );
}

// ─── DepartmentCard: nodo del departamento con sus tres grupos ───
function DepartmentCard({ data }) {
    const onOpen = data.onOpen;
    const isMando = !!data.isMando;
    const isAsistente = !!data.isAsistente;
    const rows = data.rows || [];
    const tallScroll = rows.length > MAX_ROWS_BEFORE_SCROLL;

    const cardW = data.width || NODE_WIDTH;
    return (
        <div
            style={{
                width: cardW, background: '#fff',
                border: isMando
                    ? `1px dashed ${AMBER}`
                    : isAsistente
                        ? `1px solid ${BRAND}`
                        : `1px solid ${SLATE_300}`,
                borderRadius: 8, overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                fontFamily: 'inherit',
            }}
        >
            <Handle type="target" position={Position.Top}    id="top"    style={{ opacity: 0 }} />
            <Handle type="target" position={Position.Left}   id="left"   style={{ opacity: 0 }} />
            <Handle type="target" position={Position.Right}  id="right-in" style={{ opacity: 0 }} />
            <Handle type="target" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Bottom} id="bottom-out" style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Right}  id="right"  style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Left}   id="left-out" style={{ opacity: 0 }} />

            {/* Cabecera: título centrado en el ancho TOTAL del header.
                Grid 1fr auto 1fr → la columna central queda centrada aunque
                a la derecha haya un badge (mando intermedio / asistente). */}
            <div style={{
                background: isMando ? AMBER : BRAND, color: '#fff',
                padding: '8px 12px', fontSize: 13, fontWeight: 600,
                lineHeight: 1.25, height: NODE_HEADER_H,
                display: 'grid', gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center', gap: 8,
            }}>
                <span />
                <span style={{
                    textAlign: 'center',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {data.nombre}
                </span>
                {(isMando || isAsistente) ? (
                    <span style={{
                        fontWeight: 400, fontSize: 10, opacity: 0.9,
                        justifySelf: 'end',
                    }}>
                        {isMando ? 'mando intermedio' : 'asistente'}
                    </span>
                ) : <span />}
            </div>

            {/* Filas: responsables, adjuntos, equipo */}
            <div style={{
                padding: NODE_PADDING_V, display: 'flex', flexDirection: 'column', gap: 2,
                maxHeight: tallScroll ? MAX_ROWS_BEFORE_SCROLL * ROW_H : undefined,
                overflowY: tallScroll ? 'auto' : 'visible',
            }}>
                {rows.length === 0 ? (
                    <div style={{ fontSize: 11, color: SLATE_500, fontStyle: 'italic', padding: 4 }}>
                        (sin personas asignadas)
                    </div>
                ) : (() => {
                    // Separamos "head" (responsables + adjuntos huérfanos, en
                    // su propia fila apilada) y "equipo" (miembros, en wrap a
                    // dos columnas cuando son varios). Entre head y equipo se
                    // pinta una línea divisoria si hay ambas partes.
                    const head = rows.filter(
                        (r) => r.tipo === 'pareja' || r.tipo === 'adjuntoHuerfano'
                    );
                    const equipo = rows.filter((r) => r.tipo === 'miembro');

                    const renderHead = head.map((r, i) => {
                        if (r.tipo === 'pareja') {
                            return (
                                <div
                                    key={`pareja-${r.persona.Id}-${i}`}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        flexWrap: 'nowrap',
                                    }}
                                >
                                    <div style={{ flex: '1 1 50%', minWidth: 0 }}>
                                        <PersonRow
                                            persona={r.persona}
                                            cargo={r.cargo}
                                            departamentoId={data.departamentoId}
                                            onOpen={onOpen}
                                            destacado
                                        />
                                    </div>
                                    {r.adjuntos && r.adjuntos.length > 0 && (
                                        <>
                                            <span style={{
                                                color: SLATE_500, fontSize: 16, lineHeight: 1,
                                                padding: '0 2px', flexShrink: 0,
                                            }}>←</span>
                                            <div style={{
                                                display: 'flex', flexDirection: 'column', gap: 2,
                                                flex: '1 1 50%', minWidth: 0,
                                            }}>
                                                {r.adjuntos.map((a, j) => (
                                                    <PersonRow
                                                        key={`adj-${a.persona.Id}-${j}`}
                                                        persona={a.persona}
                                                        cargo={a.cargo}
                                                        departamentoId={data.departamentoId}
                                                        onOpen={onOpen}
                                                        compact
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        }
                        // adjuntoHuerfano
                        return (
                            <div
                                key={`huerf-${r.persona.Id}-${i}`}
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                            >
                                <span style={{ color: SLATE_500, fontSize: 14, paddingLeft: 2 }}>←</span>
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <PersonRow
                                        persona={r.persona}
                                        cargo={r.cargo}
                                        departamentoId={data.departamentoId}
                                        onOpen={onOpen}
                                    />
                                </div>
                            </div>
                        );
                    });

                    // Equipo en wrap a 2 columnas (cada miembro flex 1 1 ~50%;
                    // con un solo miembro se expande a 100%).
                    const renderEquipo = equipo.length > 0 ? (
                        <div
                            key="equipo-wrap"
                            style={{
                                display: 'flex', flexWrap: 'wrap',
                                gap: 4, marginTop: 2,
                            }}
                        >
                            {equipo.map((r, i) => (
                                <div
                                    key={`m-${r.persona.Id}-${i}`}
                                    style={{
                                        flex: '1 1 calc(50% - 2px)',
                                        minWidth: '40%',
                                    }}
                                >
                                    <PersonRow
                                        persona={r.persona}
                                        cargo={r.cargo}
                                        departamentoId={data.departamentoId}
                                        onOpen={onOpen}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : null;

                    // Separador responsable ↔ equipo: doble flecha hacia abajo
                    // (ChevronsDown) centrada. Comunica visualmente "lo de
                    // arriba manda sobre lo de abajo" sin recurrir a una línea.
                    const separador = (head.length > 0 && equipo.length > 0) ? (
                        <div
                            key="sep-head-equipo"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: SLATE_500,
                                margin: '2px 0',
                                lineHeight: 0,
                            }}
                            aria-hidden="true"
                        >
                            <ChevronsDown size={14} strokeWidth={2.25} />
                        </div>
                    ) : null;

                    return [...renderHead, separador, renderEquipo].filter(Boolean);
                })()}
            </div>
        </div>
    );
}

// Estilos de los botones de la toolbar de edición del organigrama.
const btnToolbarStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#fff', border: `1px solid ${SLATE_300}`,
    borderRadius: 6, padding: '6px 10px', fontSize: 13,
    color: INK, cursor: 'pointer',
};
const btnPrimary = {
    background: BRAND, color: '#fff', borderColor: BRAND,
};

// ─── Junction invisible (asistente colgado del tronco) ───
// Acepta dimensions opcionales por data.w/data.h para que el handle 'right'
// (centrado verticalmente) coincida EXACTAMENTE con el handle 'left' del
// asistente. Sin esto, con un junction de 2x2 el centro vertical no encaja
// con el del asistente (~80 px de alto) y la línea sale ligeramente torcida.
function Junction({ data }) {
    const w = (data && data.w) || JUNCTION_SIZE;
    const h = (data && data.h) || JUNCTION_SIZE;
    return (
        <div style={{ width: w, height: h }}>
            <Handle type="target" position={Position.Top}    id="top"       style={{ opacity: 0, pointerEvents: 'none' }} />
            {/* target bottom: el bus-in entra hasta el MISMO punto del que
                sale el fan (sourceHandle 'bottom'). Sin este handle, había
                un gap de JUNCTION_SIZE px entre el final del tronco y el
                inicio del fan horizontal. */}
            <Handle type="target" position={Position.Bottom} id="bottom-in" style={{ opacity: 0, pointerEvents: 'none' }} />
            <Handle type="source" position={Position.Right}  id="right"     style={{ opacity: 0, pointerEvents: 'none' }} />
            <Handle type="source" position={Position.Bottom} id="bottom"    style={{ opacity: 0, pointerEvents: 'none' }} />
        </div>
    );
}

// ─── Nodo sintético "negocio" del que cuelgan las raíces ───
function BizRoot({ data }) {
    return (
        <div style={{
            width: NODE_WIDTH, background: '#fff',
            border: `2px solid ${BRAND}`, borderRadius: 10, padding: 12,
            textAlign: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 8,
        }}>
            <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
            {data.logo ? (
                <img
                    src={`${API_URL}/api/negocio/logo/${data.logo}`}
                    alt={data.nombre}
                    style={{ height: 72, maxWidth: '90%', objectFit: 'contain' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
            ) : null}
            <div style={{
                fontWeight: 700, color: BRAND, fontSize: 16,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                maxWidth: '100%',
            }}>
                {data.nombre || 'Negocio'}
            </div>
        </div>
    );
}

// ─── Bracket "Departamento Comunes" ───────────────────────────────
// Conector ancho que abarca todo el ancho del árbol principal y baja al
// centro del header de "Departamento Comunes". Se renderiza con SVG
// dentro del propio nodo: una horizontal larga y una vertical central.
// Se posiciona y dimensiona desde buildGraph; aquí solo pinta.
function BracketComunes({ data }) {
    const w = (data && data.w) || 100;
    const h = (data && data.h) || 100;
    const dropX = (data && typeof data.dropX === 'number') ? data.dropX : w / 2;
    // La horizontal cae en el tercio superior para dejar aire visual antes
    // del header del bloque Comunes.
    const horizY = Math.round(h * 0.35);
    return (
        <div style={{ width: w, height: h, pointerEvents: 'none' }}>
            <svg width={w} height={h} style={{ overflow: 'visible' }}>
                <line
                    x1={0} y1={horizY} x2={w} y2={horizY}
                    stroke={SLATE_500} strokeWidth={1.5}
                />
                <line
                    x1={dropX} y1={horizY} x2={dropX} y2={h}
                    stroke={SLATE_500} strokeWidth={1.5}
                />
            </svg>
        </div>
    );
}

const nodeTypes = {
    dept: DepartmentCard,
    junction: Junction,
    bizroot: BizRoot,
    bracketComunes: BracketComunes,
};

// ─── Edge custom: stub horizontal del asistente ────────────────────
// Usado para la línea Junction → asistente. Ignora la Y del source
// (que dependía de la altura calculada de antemano para el junction y
// que NO coincide exactamente con la altura DOM del card) y traza la
// línea a la Y EXACTA del handle target (donde React Flow ya conoce
// el handle 'left' del asistente). Así sale perfectamente horizontal,
// sin codo residual.
function StubAsistenteEdge({ sourceX, targetX, targetY, style = {} }) {
    const d = `M ${sourceX},${targetY} L ${targetX},${targetY}`;
    return (
        <path
            d={d}
            fill="none"
            stroke={style.stroke || SLATE_500}
            strokeWidth={style.strokeWidth || 1.5}
            strokeDasharray={style.strokeDasharray}
        />
    );
}

const edgeTypes = {
    stubAsistente: StubAsistenteEdge,
};

// ─── Construcción de filas (responsable / adjuntos / equipo) ───
// Devuelve un array de filas VISUALES con tipo discriminado:
//   { tipo: 'pareja', persona, cargo, adjuntos: [{persona, cargo}] }
//   { tipo: 'adjuntoHuerfano', persona, cargo }
//   { tipo: 'miembro', persona, cargo }
// El render del card decide cómo presentar cada tipo. Una "pareja" ocupa una
// sola fila horizontal: responsable a la izquierda, flecha ←, adjuntos a la
// derecha.
function rowsForDept(personasDept, departamentoId) {
    // BBDD puede devolver booleanos como 0/1, y los IDs en `c.adjunto` pueden
    // venir como string o number — normalizamos con !! y Number().
    const adjuntos = personasDept.filter((p) => {
        const c = cargoDe(p, departamentoId);
        return c && c.adjunto;
    });

    // IDs de personas apuntadas por algún adjunto del mismo departamento.
    // Si una persona es señalada como "jefe" por un adjunto, la tratamos como
    // responsable aunque su flag `responsable` no esté marcado en BBDD.
    const idsApuntadosPorAdjunto = new Set();
    adjuntos.forEach((a) => {
        const ca = cargoDe(a, departamentoId);
        if (ca && ca.adjunto != null) idsApuntadosPorAdjunto.add(Number(ca.adjunto));
    });

    const responsables = personasDept.filter((p) => {
        const c = cargoDe(p, departamentoId);
        if (!c || c.adjunto) return false;
        return !!c.responsable || idsApuntadosPorAdjunto.has(Number(p.Id));
    });
    const responsablesSet = new Set(responsables.map((p) => p.Id));
    const equipo = personasDept.filter((p) => {
        const c = cargoDe(p, departamentoId);
        if (!c || c.adjunto) return false;
        return !responsablesSet.has(p.Id);
    });

    // Adjuntos pegados a su responsable (por Cargo.adjunto = id_responsable).
    const adjuntosDe = (rid) => adjuntos.filter((a) => {
        const ca = cargoDe(a, departamentoId);
        return ca && Number(ca.adjunto) === Number(rid);
    });

    const out = [];
    const adjuntosUsados = new Set();
    responsables.forEach((r) => {
        const cr = cargoDe(r, departamentoId);
        const adjs = adjuntosDe(r.Id).map((a) => {
            adjuntosUsados.add(a.Id);
            const ca = cargoDe(a, departamentoId);
            return { persona: a, cargo: ca?.cargo };
        });
        out.push({ tipo: 'pareja', persona: r, cargo: cr?.cargo, adjuntos: adjs });
    });
    // Adjuntos huérfanos (su responsable ya no existe) — al final del bloque.
    adjuntos.forEach((a) => {
        if (adjuntosUsados.has(a.Id)) return;
        const ca = cargoDe(a, departamentoId);
        out.push({ tipo: 'adjuntoHuerfano', persona: a, cargo: ca?.cargo });
    });
    equipo.forEach((e) => {
        const ce = cargoDe(e, departamentoId);
        out.push({ tipo: 'miembro', persona: e, cargo: ce?.cargo });
    });
    return out;
}

// Identificación del bloque "Comunes". Se aísla del árbol principal y se pinta
// debajo, como bloque separado, con su propio layout y un conector desde el
// bizroot a su cabecera. De momento por nombre — sustituir por un flag en BBDD
// cuando exista (TODO: comun=true en departamentos_empresas/departamentos).
const NOMBRE_BLOQUE_COMUNES = 'Departamento Comunes';
// Separación vertical entre la base del árbol principal y la cabecera del
// bloque comunes (visualmente notable, pero sin abrirse demasiado).
const SEP_BLOQUE_COMUNES = 200;

// ─── Helper: alinea los bordes SUPERIORES de los nodos de un mismo rango.
// dagre devuelve `n.y` como CENTRO; si cada nodo tiene altura distinta y
// usamos `n.y - height/2` cada uno acaba con un top distinto y las líneas
// salen descuadradas. Agrupando por `n.y` (todos los nodos del mismo rank
// tienen el mismo centro en TB) y usando `maxH` del rango, los bordes
// superiores quedan a la misma altura. Las tarjetas crecen hacia abajo.
function alignTopsByRank(ids, gNodeFn) {
    const groups = new Map();
    for (const id of ids) {
        const n = gNodeFn(id);
        if (!n) continue;
        const key = Math.round(n.y);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ id, n });
    }
    const tops = new Map();
    for (const [, arr] of groups) {
        const maxH = Math.max(...arr.map((x) => x.n.height));
        arr.forEach(({ id, n }) => tops.set(id, n.y - maxH / 2));
    }
    return tops;
}

// ─── BUILDER: datos → { nodes, edges } ───
function buildGraph({ negocio, departamentos, asesorias, personas, onOpenPersona }) {
    if (!negocio || !Array.isArray(departamentos) || departamentos.length === 0) {
        return { nodes: [], edges: [] };
    }

    // Diccionarios.
    const depsEmpresa = []; // {de, dep} para cada Departamento del negocio
    departamentos.forEach((dep) => {
        const de = dep && dep.Negocios && dep.Negocios[0] && dep.Negocios[0].DepartamentoEmpresa;
        if (de) depsEmpresa.push({ de, dep });
    });
    const byDeId = new Map(); // de.id → { de, dep }
    depsEmpresa.forEach((x) => byDeId.set(x.de.id, x));

    // Identificar bloque "Comunes" y su subárbol (BFS hijos por cualquier FK).
    const comunesRoot = depsEmpresa.find((x) => x.dep.nombre === NOMBRE_BLOQUE_COMUNES);
    const idsBloqueComunes = new Set();
    if (comunesRoot) {
        const frontier = [comunesRoot.de.id];
        while (frontier.length) {
            const cur = frontier.shift();
            if (idsBloqueComunes.has(cur)) continue;
            idsBloqueComunes.add(cur);
            depsEmpresa.forEach((x) => {
                const { de } = x;
                if (de.dependencia_id === cur || de.mando_intermedio_de_1 === cur || de.asistente_de === cur) {
                    frontier.push(de.id);
                }
            });
        }
    }
    const enBloqueComunes = (id) => idsBloqueComunes.has(id);

    // Clasificar SOLO los que NO están en el bloque comunes (el principal).
    const lineasMain = [];  // dependencia_id != null
    const mandosMain = [];  // mando_intermedio_de_1 != null
    const asistsMain = [];  // asistente_de != null
    const raicesMain = [];  // ninguno de los tres
    // El bloque comunes se procesa aparte; clasificamos sus líneas internas.
    const lineasComunes = [];
    const mandosComunes = [];
    const asistsComunes = [];
    depsEmpresa.forEach((x) => {
        const { de } = x;
        if (enBloqueComunes(de.id)) {
            // No clasificamos el nodo Comunes en líneas (lo tratamos como raíz del sub-layout);
            // sus descendientes sí van a líneas internas del bloque.
            if (de.id === comunesRoot.de.id) return;
            if (de.asistente_de) asistsComunes.push(x);
            else if (de.mando_intermedio_de_1) mandosComunes.push(x);
            else if (de.dependencia_id) lineasComunes.push(x);
            return;
        }
        if (de.asistente_de) asistsMain.push(x);
        else if (de.mando_intermedio_de_1) mandosMain.push(x);
        else if (de.dependencia_id) lineasMain.push(x);
        else raicesMain.push(x);
    });

    const onOpen = (persona, departamentoId) => {
        if (onOpenPersona) onOpenPersona(persona, departamentoId);
    };

    // Alturas estimadas por número de filas, acotadas por scroll.
    const heightOf = (rowsCount) => {
        const filas = Math.min(rowsCount, MAX_ROWS_BEFORE_SCROLL);
        const base = NODE_HEADER_H + NODE_PADDING_V * 2 + 8;
        if (filas === 0) return base + 28;
        return base + filas * ROW_H;
    };

    // Filas, alturas y anchos reales por departamento.
    // Solo ensanchamos las cajas que contienen alguna pareja "responsable + adjunto(s)".
    const rowsByDeId = new Map();
    const heightByDeId = new Map();
    const widthByDeId = new Map();
    depsEmpresa.forEach((x) => {
        const persDept = personasDe(personas, x.dep.Id);
        const rows = rowsForDept(persDept, x.dep.Id);
        rowsByDeId.set(x.de.id, rows);
        // Filas visuales: head ocupa 1 fila cada uno; los miembros del equipo
        // van en wrap a 2 columnas, así que cuentan ceil(equipo/2).
        const equipoCount = rows.filter((r) => r.tipo === 'miembro').length;
        const headCount = rows.length - equipoCount;
        const filasVisuales = headCount + Math.ceil(equipoCount / 2);
        heightByDeId.set(x.de.id, heightOf(filasVisuales));
        const tieneAdjunto = rows.some(
            (r) => r.tipo === 'pareja' && Array.isArray(r.adjuntos) && r.adjuntos.length > 0
        );
        widthByDeId.set(x.de.id, tieneAdjunto ? NODE_WIDTH_WIDE : NODE_WIDTH);
    });
    const widthOfId = (idStr) => {
        if (idStr === 'biz-root') return NODE_WIDTH;
        const n = Number(idStr);
        return widthByDeId.get(n) || NODE_WIDTH;
    };

    // ─── Layout PRINCIPAL: bizroot + tronco main (líneas + raíces) ───
    // El asistente NO entra en dagre — se coloca manualmente lateral a su
    // padre (con detección de colisiones AABB). Los hijos del padre siguen
    // colgando del padre, no del asistente.
    const troncoIdsMain = new Set();
    raicesMain.forEach((x) => troncoIdsMain.add(x.de.id));
    lineasMain.forEach((x) => troncoIdsMain.add(x.de.id));

    const BIZ = 'biz-root';
    const BIZ_HEIGHT = 56;

    const gMain = new dagre.graphlib.Graph();
    gMain.setGraph({ rankdir: 'TB', ranksep: 180, nodesep: 60 });
    gMain.setDefaultEdgeLabel(() => ({}));
    gMain.setNode(BIZ, { width: NODE_WIDTH, height: BIZ_HEIGHT });
    troncoIdsMain.forEach((id) => {
        gMain.setNode(String(id), {
            width: widthByDeId.get(id) || NODE_WIDTH,
            height: heightByDeId.get(id) || 80,
        });
    });
    raicesMain.forEach((x) => gMain.setEdge(BIZ, String(x.de.id)));
    lineasMain.forEach((x) => {
        const padre = x.de.dependencia_id;
        if (troncoIdsMain.has(padre)) gMain.setEdge(String(padre), String(x.de.id));
        else gMain.setEdge(BIZ, String(x.de.id)); // padre desconocido o en otro bloque → cuelga del biz
    });
    dagre.layout(gMain);

    // Alineación por borde superior (tronco main + biz).
    const topsMain = alignTopsByRank([BIZ, ...[...troncoIdsMain].map(String)], (id) => gMain.node(id));

    const positions = {};
    positions[BIZ] = {
        x: gMain.node(BIZ).x - NODE_WIDTH / 2,
        y: topsMain.get(BIZ),
    };
    troncoIdsMain.forEach((id) => {
        const n = gMain.node(String(id));
        positions[String(id)] = {
            x: n.x - (widthByDeId.get(id) || NODE_WIDTH) / 2,
            y: topsMain.get(String(id)),
        };
    });

    // Bajar el bizroot para que el hueco visual entre el logo del negocio
    // y las primeras raíces (Dirección General, Consejo Asesor…) se quede
    // a la mitad. Como ya quitamos la línea de mando bizroot → raíces,
    // ese gap se notaba excesivo. Solo se mueve el bizroot — el resto del
    // árbol mantiene sus posiciones, así no afecta a ranksep ni a los
    // niveles más bajos (donde el asistente necesita su aire).
    {
        const bizBottom = positions[BIZ].y + BIZ_HEIGHT;
        const topsRaices = raicesMain
            .map((x) => positions[String(x.de.id)] && positions[String(x.de.id)].y)
            .filter((v) => typeof v === 'number');
        if (topsRaices.length > 0) {
            const minRaizTop = Math.min(...topsRaices);
            const gap = minRaizTop - bizBottom;
            if (gap > 0) {
                positions[BIZ].y += gap / 2;
            }
        }
    }

    // Bracket conector main → "Departamento Comunes": se calcula dentro
    // del bloque if (comunesRoot) y se materializa más abajo, cuando ya
    // existe el array `nodes`.
    let bracketComunesData = null;

    // ─── Layout BLOQUE COMUNES (si existe) ───
    if (comunesRoot) {
        const troncoIdsComunes = new Set([comunesRoot.de.id]);
        lineasComunes.forEach((x) => troncoIdsComunes.add(x.de.id));

        const gCom = new dagre.graphlib.Graph();
        gCom.setGraph({ rankdir: 'TB', ranksep: 180, nodesep: 60 });
        gCom.setDefaultEdgeLabel(() => ({}));
        troncoIdsComunes.forEach((id) => {
            gCom.setNode(String(id), {
                width: widthByDeId.get(id) || NODE_WIDTH,
                height: heightByDeId.get(id) || 80,
            });
        });
        lineasComunes.forEach((x) => {
            const padre = x.de.dependencia_id;
            if (troncoIdsComunes.has(padre)) gCom.setEdge(String(padre), String(x.de.id));
            else gCom.setEdge(String(comunesRoot.de.id), String(x.de.id)); // defensivo
        });
        dagre.layout(gCom);

        const topsCom = alignTopsByRank([...troncoIdsComunes].map(String), (id) => gCom.node(id));

        // Posiciones provisionales del bloque comunes; las desplazamos luego para
        // colgarlas debajo del árbol principal y centradas con él.
        const posTmp = {};
        troncoIdsComunes.forEach((id) => {
            const n = gCom.node(String(id));
            posTmp[String(id)] = {
                x: n.x - (widthByDeId.get(id) || NODE_WIDTH) / 2,
                y: topsCom.get(String(id)),
            };
        });

        // bbox provisional del bloque (solo necesitamos extremos para offset).
        const xs = [], ys_top = [];
        troncoIdsComunes.forEach((id) => {
            const p = posTmp[String(id)];
            const w = widthByDeId.get(id) || NODE_WIDTH;
            xs.push(p.x); xs.push(p.x + w);
            ys_top.push(p.y);
        });
        const bbXmin = Math.min(...xs);
        const bbXmax = Math.max(...xs);
        const bbYmin = Math.min(...ys_top);

        // bbox del bloque principal (para calcular maxY y centro X).
        const xsMain = [], ysBotMain = [];
        const idsPosMain = [BIZ, ...[...troncoIdsMain].map(String)];
        idsPosMain.forEach((id) => {
            const p = positions[id];
            const h = id === BIZ ? BIZ_HEIGHT : (heightByDeId.get(Number(id)) || heightByDeId.get(id) || 80);
            const w = widthOfId(id);
            xsMain.push(p.x); xsMain.push(p.x + w);
            ysBotMain.push(p.y + h);
        });
        const mainXmin = Math.min(...xsMain);
        const mainXmax = Math.max(...xsMain);
        const mainYmax = Math.max(...ysBotMain);

        const offsetY = (mainYmax + SEP_BLOQUE_COMUNES) - bbYmin;
        const offsetX = ((mainXmin + mainXmax) / 2) - ((bbXmin + bbXmax) / 2);

        // Aplicar offset y publicar en positions.
        troncoIdsComunes.forEach((id) => {
            const p = posTmp[String(id)];
            positions[String(id)] = { x: p.x + offsetX, y: p.y + offsetY };
        });

        // ─── Bracket "Departamento Comunes" ─────────────────────────
        // Conector ancho que abarca el árbol principal de arriba y baja
        // al centro de la cabecera de Comunes. Se calcula DESPUÉS de
        // aplicar offset (la cabecera de Comunes ya tiene posición final);
        // el nodo bracketComunes se inserta más abajo, cuando el array
        // `nodes` ya existe.
        const comunesRootPos = positions[String(comunesRoot.de.id)];
        if (comunesRootPos) {
            const comunesRootW = widthByDeId.get(comunesRoot.de.id) || NODE_WIDTH;
            const PAD_TOP = 40;      // espacio bajo el árbol principal
            const PAD_BOTTOM = 20;   // espacio sobre la cabecera de Comunes
            const bracketX = mainXmin;
            const bracketY = mainYmax + PAD_TOP;
            const bracketW = Math.max(mainXmax - mainXmin, 1);
            const bracketH = Math.max(
                (comunesRootPos.y - PAD_BOTTOM) - bracketY,
                10
            );
            const comunesCenterAbs = comunesRootPos.x + comunesRootW / 2;
            const dropX = comunesCenterAbs - bracketX;
            bracketComunesData = {
                position: { x: bracketX, y: bracketY },
                data: { w: bracketW, h: bracketH, dropX },
            };
        }
    }

    // ─── Construir nodos React Flow ───
    const nodes = [];
    // Bracket conector main → Comunes (si aplica). Va PRIMERO para
    // garantizar que se pinta detrás de las cajas (z-order por orden de
    // inserción en React Flow). No tiene handles ni edges; solo es el
    // SVG del conector ancho.
    if (bracketComunesData) {
        nodes.push({
            id: '__bracket-comunes',
            type: 'bracketComunes',
            position: bracketComunesData.position,
            data: bracketComunesData.data,
            draggable: false, selectable: false, focusable: false,
        });
    }
    // Bizroot: marcamos data.deId con el sentinel '__biz' para que el
    // override del map de posiciones manuales lo trate igual que los demás
    // nodos. Su `draggable` lo gobierna `nodesDraggable` del ReactFlow
    // (igual que el resto en modo edición).
    edgesPush(nodes, BIZ, 'bizroot', positions[BIZ], {
        nombre: negocio.nombre,
        logo: negocio.logo,
        deId: '__biz',
    }, { selectable: false });

    // Rectángulos ya colocados (BIZ + troncos main + troncos comunes).
    // Los mando/asistente se posicionan lateralmente y comprobamos colisión
    // AABB con esta lista para desplazarlos hacia la derecha hasta encontrar
    // hueco: evita que el "asistente de A" caiga sobre "B" cuando A y B son
    // hermanos puestos lado a lado por dagre.
    const rectsColocados = [];
    rectsColocados.push({
        x: positions[BIZ].x, y: positions[BIZ].y,
        w: NODE_WIDTH, h: BIZ_HEIGHT,
    });
    depsEmpresa.forEach((x) => {
        const p = positions[String(x.de.id)];
        if (!p) return;
        rectsColocados.push({
            x: p.x, y: p.y,
            w: widthByDeId.get(x.de.id) || NODE_WIDTH,
            h: heightByDeId.get(x.de.id) || 80,
        });
    });
    const GAP_LATERAL = 40;
    const intersecta = (a, b) =>
        a.x < b.x + b.w && a.x + a.w > b.x &&
        a.y < b.y + b.h && a.y + a.h > b.y;
    const buscarHuecoX = (xIni, y, w, h) => {
        let x = xIni;
        // Hasta 12 intentos: en árboles normales basta con 2-3.
        for (let k = 0; k < 12; k++) {
            const candidato = { x, y, w, h };
            const choque = rectsColocados.find((r) => intersecta(candidato, r));
            if (!choque) return x;
            x = choque.x + choque.w + GAP_LATERAL;
        }
        return x;
    };

    // Tronco main + tronco comunes (los que ya tienen posición).
    depsEmpresa.forEach((x) => {
        const { de, dep } = x;
        const enBlComunes = enBloqueComunes(de.id);
        const inTroncoMain = !enBlComunes && troncoIdsMain.has(de.id);
        const inTroncoComunes = enBlComunes && positions[String(de.id)] !== undefined;
        let pos;
        if (inTroncoMain || inTroncoComunes) {
            pos = positions[String(de.id)];
        } else {
            // Mando o asistente — posición manual al lado del padre.
            const padreId = de.asistente_de || de.mando_intermedio_de_1;
            const padrePos = positions[String(padreId)];
            const propioW = widthByDeId.get(de.id) || NODE_WIDTH;
            const propioH = heightByDeId.get(de.id) || 80;
            if (!padrePos) {
                pos = { x: positions[BIZ].x + NODE_WIDTH + 80, y: positions[BIZ].y + 100 };
            } else if (de.asistente_de) {
                // Asistente: DESPLAZADO al lateral derecho del padre, en su
                // propia banda CERCA del padre — no en el centro del gap
                // padre/hijos. Así queda hueco claro debajo del asistente
                // antes de la fila de departamentos, y no parece su padre.
                // El stub que lo conecta lo dibuja el junction (más abajo).
                const padreW = widthByDeId.get(padreId) || NODE_WIDTH;
                const padreH = heightByDeId.get(padreId) || 80;
                // Top del asistente fijo a 50 px del bottom del padre.
                // Con ranksep=300 sobra hueco hacia los hijos.
                const GAP_PADRE_ASIST = 50;
                const topY = padrePos.y + padreH + GAP_PADRE_ASIST;
                pos = { x: padrePos.x + padreW + 80, y: topY };
            } else {
                // Mando intermedio: lateral con AABB.
                const offsetX = (widthByDeId.get(padreId) || NODE_WIDTH) + 80;
                const xIni = padrePos.x + offsetX;
                const y = padrePos.y + 30;
                const xLibre = buscarHuecoX(xIni, y, propioW, propioH);
                pos = { x: xLibre, y };
            }
            // Guardamos la posición (la usan asesorías / asistentes después)
            // y la registramos como rectángulo ocupado para futuros laterales.
            positions[String(de.id)] = pos;
            rectsColocados.push({ x: pos.x, y: pos.y, w: propioW, h: propioH });
        }
        edgesPush(nodes, String(de.id), 'dept', pos, {
            nombre: dep.nombre,
            departamentoId: dep.Id,
            deId: de.id, // PK del DepartamentoEmpresa — key del map de posiciones manuales
            rows: rowsByDeId.get(de.id) || [],
            isMando: !!de.mando_intermedio_de_1,
            isAsistente: !!de.asistente_de,
            width: widthByDeId.get(de.id) || NODE_WIDTH,
            onOpen,
        });
    });

    // Asesorías → asistentes: alinear el asesor con el asesorado en X.
    // Cuando un asesor tiene una asesoría que llega a un ASISTENTE, se
    // mueve al asesor para que su centro X coincida con el centro X del
    // asistente. Así la línea de asesoría baja vertical y recta, sin
    // diagonales raras. Esto solo se aplica si el asesor no tiene padre
    // jerárquico (es raíz): un asesor con sus propios hijos no se mueve
    // para no romper su rama. También se reposiciona el nodo en `nodes`
    // si ya estaba construido.
    (asesorias || []).forEach((a) => {
        if (!a || !a.asesor_de_id || !a.asesorado_de_id) return;
        const asesoradoId = a.asesorado_de_id;
        const asesorId = a.asesor_de_id;
        const asesoradoEntry = byDeId.get(asesoradoId);
        const asesorEntry = byDeId.get(asesorId);
        if (!asesoradoEntry || !asesorEntry) return;
        // Solo si el asesorado es un asistente.
        if (!asesoradoEntry.de.asistente_de) return;
        // Solo si el asesor es raíz (sin dependencia, sin mando, sin
        // asistente). Mover una rama con hijos descolocaría todo.
        const sd = asesorEntry.de;
        if (sd.dependencia_id || sd.mando_intermedio_de_1 || sd.asistente_de) return;
        const posAsesor = positions[String(asesorId)];
        const posAsesorado = positions[String(asesoradoId)];
        if (!posAsesor || !posAsesorado) return;
        const wAsesor = widthByDeId.get(asesorId) || NODE_WIDTH;
        const wAsesorado = widthByDeId.get(asesoradoId) || NODE_WIDTH;
        const centroAsesorado = posAsesorado.x + wAsesorado / 2;
        posAsesor.x = centroAsesorado - wAsesor / 2;
    });

    // Aristas.
    const SOLID = { stroke: SLATE_500, strokeWidth: 1.5 };
    const MANDO_LINE = { stroke: AMBER, strokeWidth: 1.5, strokeDasharray: '6 4' };
    const ASESORIA_LINE = { stroke: '#0ea5e9', strokeWidth: 1.4, strokeDasharray: '4 4' };
    const edges = [];

    // Bus de reparto: para cada padre con asistente y >= 1 hijo, creamos un
    // junction invisible sobre el tronco del padre a una Y BAJA (cerca de la
    // fila de hijos). La arista padre → bus baja vertical por el tronco; el
    // fan a cada hijo sale del bus, así el "bus" horizontal queda a la Y
    // exacta del junction. Solo cambia el routing — ningún nodo real se
    // mueve, dagre intacto.
    const padresConAsistente = new Set();
    [...asistsMain, ...asistsComunes].forEach((y) =>
        padresConAsistente.add(y.de.asistente_de)
    );
    const busJunctionIds = new Map(); // padreId numérico → id del junction
    padresConAsistente.forEach((padreId) => {
        const padrePos = positions[String(padreId)];
        if (!padrePos) return;
        const hijos = depsEmpresa.filter((z) =>
            Number(z.de.dependencia_id) === Number(padreId)
        );
        if (hijos.length === 0) return;
        const primerHijoPos = hijos
            .map((h) => positions[String(h.de.id)])
            .find(Boolean);
        if (!primerHijoPos) return;
        const padreW = widthByDeId.get(padreId) || NODE_WIDTH;
        const busX = padrePos.x + padreW / 2;
        // Bus 60 px arriba del top del primer hijo — bien cerca de la fila.
        const busY = primerHijoPos.y - 60;
        const busId = `__bus-${padreId}`;
        nodes.push({
            id: busId, type: 'junction',
            position: { x: busX - JUNCTION_SIZE / 2, y: busY - JUNCTION_SIZE / 2 },
            data: {}, draggable: false, selectable: false, focusable: false,
        });
        busJunctionIds.set(Number(padreId), busId);
        // padre.bottom → bus.bottom (tramo vertical del tronco).
        // El target es 'bottom-in' (no 'top') para que el tronco llegue al
        // MISMO punto del que sale el fan horizontal. Así no queda gap.
        edges.push({
            id: `bus-in-${padreId}`,
            source: String(padreId), sourceHandle: 'bottom-out',
            target: busId, targetHandle: 'bottom-in',
            type: 'smoothstep', style: SOLID,
        });
    });

    // Raíces del main: NO se pinta arista desde el bizroot a las raíces.
    // El nodo LAE INNOVA solo identifica el negocio; los departamentos
    // raíz (Dirección General, Consejo Asesor…) no dependen jerárquicamente
    // de él, así que no debe haber línea de mando hacia ellos. Sigue
    // habiendo setEdge(BIZ, …) en dagre para que el LAYOUT los coloque
    // debajo del bizroot, pero no se renderiza ninguna línea.
    // Líneas del main. Si el padre tiene bus, la arista sale del bus junction
    // (no del padre); el "fan" horizontal queda exactamente a la Y del bus.
    lineasMain.forEach((x) => {
        const padre = x.de.dependencia_id;
        if (!troncoIdsMain.has(padre)) return; // ya cuelga del biz
        const busId = busJunctionIds.get(Number(padre));
        if (busId) {
            edges.push({
                id: `dep-${padre}-${x.de.id}`,
                source: busId, sourceHandle: 'bottom',
                target: String(x.de.id),
                type: 'smoothstep', style: SOLID,
            });
        } else {
            edges.push({
                id: `dep-${padre}-${x.de.id}`,
                source: String(padre), target: String(x.de.id),
                type: 'smoothstep', style: SOLID,
            });
        }
    });
    // Líneas del bloque comunes (internas).
    lineasComunes.forEach((x) => {
        const padreReal = x.de.dependencia_id;
        const padreEnBloque = idsBloqueComunes.has(padreReal);
        const sourceId = padreEnBloque ? String(padreReal) : String(comunesRoot.de.id);
        const busId = padreEnBloque ? busJunctionIds.get(Number(padreReal)) : null;
        if (busId) {
            edges.push({
                id: `dep-${sourceId}-${x.de.id}`,
                source: busId, sourceHandle: 'bottom',
                target: String(x.de.id),
                type: 'smoothstep', style: SOLID,
            });
        } else {
            edges.push({
                id: `dep-${sourceId}-${x.de.id}`,
                source: sourceId, target: String(x.de.id),
                type: 'smoothstep', style: SOLID,
            });
        }
    });
    // El bloque "Comunes" se pinta debajo SIN conector visual desde el bizroot,
    // para evitar la línea que atraviesa el árbol principal de arriba a abajo.
    // Mando intermedio lateral (línea naranja discontinua) — main + comunes.
    [...mandosMain, ...mandosComunes].forEach((x) => {
        const padreId = x.de.mando_intermedio_de_1;
        if (!byDeId.has(padreId)) return;
        edges.push({
            id: `mando-${padreId}-${x.de.id}`,
            source: String(padreId), sourceHandle: 'right',
            target: String(x.de.id), targetHandle: 'left',
            type: 'smoothstep', style: MANDO_LINE,
        });
    });
    // Asistente: patrón junction del spike original. Un nodo invisible se
    // pone sobre el tronco del padre (a la altura del asistente) y emite un
    // stub horizontal sólido hacia el asistente. Así el tronco padre → hijos
    // pasa limpio y el asistente queda colgado lateralmente.
    [...asistsMain, ...asistsComunes].forEach((x) => {
        const padreId = x.de.asistente_de;
        if (!padreId || !byDeId.has(padreId)) return;
        const padrePos = positions[String(padreId)];
        const asistPos = positions[String(x.de.id)];
        if (!padrePos || !asistPos) return;
        const padreW = widthByDeId.get(padreId) || NODE_WIDTH;
        const propioH = heightByDeId.get(x.de.id) || 80;
        const trunkCenterX = padrePos.x + padreW / 2;
        // Junction "alto" (mismo h que el asistente) y muy estrecho.
        // Posición top = asistente.top → handle 'right' (centrado vertical
        // del junction) coincide exactamente con el handle 'left' del
        // asistente. La línea horizontal sale recta SIN codo.
        const jxW = 2;
        const jxH = propioH;
        const junctionId = `__jx-${x.de.id}`;
        nodes.push({
            id: junctionId, type: 'junction',
            position: { x: trunkCenterX - jxW / 2, y: asistPos.y },
            data: { w: jxW, h: jxH },
            draggable: false, selectable: false, focusable: false,
        });
        edges.push({
            id: `jx-in-${x.de.id}`,
            source: String(padreId), sourceHandle: 'bottom-out',
            target: junctionId, targetHandle: 'top',
            type: 'smoothstep', style: SOLID,
        });
        edges.push({
            id: `jx-out-${x.de.id}`,
            source: junctionId, sourceHandle: 'right',
            target: String(x.de.id), targetHandle: 'left',
            type: 'stubAsistente', style: SOLID,
        });
    });
    // Asesorías: línea punteada continua del asesor al asesorado.
    // - Deduplicadas por par (asesor, asesorado): si el backend envía la
    //   misma relación dos veces, solo se pinta una arista.
    // - Tipo bezier (`default`), no smoothstep: en distancias cortas el
    //   step se fragmenta visualmente y aparecen 2-3 rayas sueltas.
    const asesoriasUnicas = new Map();
    (asesorias || []).forEach((a) => {
        if (!a || !a.asesor_de_id || !a.asesorado_de_id) return;
        const key = `${a.asesor_de_id}-${a.asesorado_de_id}`;
        if (!asesoriasUnicas.has(key)) asesoriasUnicas.set(key, a);
    });
    asesoriasUnicas.forEach((a) => {
        if (!byDeId.has(a.asesor_de_id) || !byDeId.has(a.asesorado_de_id)) return;
        // La línea visual SIEMPRE baja: source = caja con menor Y, target =
        // caja con mayor Y. Así sale por debajo del nodo de arriba y entra
        // por arriba del nodo de abajo, sin importar el sentido lógico
        // (asesor/asesorado) con el que se haya guardado la relación.
        // Esto hace la vista coherente aunque la asesoría se haya creado
        // con los selects invertidos.
        const posA = positions[String(a.asesor_de_id)];
        const posB = positions[String(a.asesorado_de_id)];
        let sourceId, targetId;
        if (posA && posB && posA.y > posB.y) {
            sourceId = String(a.asesorado_de_id);
            targetId = String(a.asesor_de_id);
        } else {
            sourceId = String(a.asesor_de_id);
            targetId = String(a.asesorado_de_id);
        }
        edges.push({
            id: `as-${a.asesor_de_id}-${a.asesorado_de_id}`,
            source: sourceId, sourceHandle: 'bottom-out',
            target: targetId, targetHandle: 'top',
            type: 'smoothstep', style: SOLID,
        });
    });

    return { nodes, edges };
}

// Helper para empujar nodos con menos boilerplate.
function edgesPush(nodes, id, type, position, data, extras = {}) {
    nodes.push({ id, type, position, data, ...extras });
}

// ─── Modal de detalle de persona (D) ───
function PersonaDialog({ open, onClose, persona, departamentos, onCenter, esStaff, onVerFicha }) {
    if (!persona) return null;
    const departamentoId = persona._departamentoIdContexto || null;
    const cargo = cargoDe(persona, departamentoId);
    const departamento = (Array.isArray(departamentos) ? departamentos : [])
        .find((d) => d.Id === departamentoId);
    const nombreDepartamento = departamento?.nombre || 'No asignado';
    const negocioNombre = persona._negocioContexto || '';
    const tieneFoto = !!persona.foto;
    const activo = persona.activo === true || persona.activo === 1
        || (persona.activo == null ? true : !!persona.activo);
    const cargoTexto = cargo?.cargo || 'Sin cargo asignado';
    const ubicacion = [persona.poblacion, persona.provincia].filter(Boolean).join(', ');
    const oficinaNombre = persona?.Oficina?.nombre || persona?.oficina?.nombre || '';
    const ubicacionLinea = [ubicacion, oficinaNombre].filter(Boolean).join(' · ');
    const { bg: avatarBg, fg: avatarFg } = avatarColor(persona);

    const AVATAR_SIZE = 92;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            {/* Cabecera #003c66, sin contenido salvo el botón de cierre. */}
            <DialogTitle sx={{
                m: 0, bgcolor: BRAND, color: 'white',
                py: 1.5, px: 2, height: 56,
            }}>
                <IconButton onClick={onClose} sx={{
                    color: 'white', position: 'absolute', right: 8, top: 8,
                }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Contenido apilado vertical: avatar arriba (a la izquierda, en su
                propia fila), después el bloque de nombre/cargo/depto, y abajo
                la sección de contacto. Avatar en flujo normal con margin-top
                negativo — solapa solo el borde inferior de la cabecera, sin
                position: absolute, sin centrado y sin pisar ningún texto. */}
            <DialogContent sx={{ pt: 2, pb: 2, px: 3, overflow: 'visible' }}>
                <div style={{
                    position: 'relative', zIndex: 2,
                    marginTop: -AVATAR_SIZE / 2,
                    width: AVATAR_SIZE, height: AVATAR_SIZE,
                }}>
                    <div style={{
                        width: AVATAR_SIZE, height: AVATAR_SIZE,
                        borderRadius: '50%', overflow: 'hidden',
                        border: '4px solid #fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        background: tieneFoto ? '#fff' : avatarBg,
                        color: avatarFg,
                        display: 'grid', placeItems: 'center',
                        fontWeight: 700, fontSize: 30, lineHeight: 1,
                    }}>
                        {tieneFoto ? (
                            <img
                                src={`${API_URL}/api/persona/imagen/${persona.foto}?formato=grande`}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ) : (
                            siniciales(persona)
                        )}
                    </div>
                    {cumpleHoy(persona) && (
                        <img
                            src={GorraCumpleanos}
                            alt=""
                            aria-hidden
                            style={{
                                position: 'absolute',
                                top: -22, right: -16,
                                width: 56, height: 56,
                                transform: 'rotate(25deg)',
                                pointerEvents: 'none',
                                zIndex: 3,
                            }}
                        />
                    )}
                </div>

                <div style={{ marginTop: 12 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
                            {nombrePersona(persona)}
                        </Typography>
                        <Chip
                            size="small"
                            label={activo ? 'Activo' : 'Inactivo'}
                            sx={{
                                bgcolor: activo ? '#dcfce7' : '#e2e8f0',
                                color: activo ? '#15803d' : '#475569',
                                fontWeight: 600, height: 22,
                            }}
                        />
                    </div>
                    <Typography sx={{ color: SLATE_500, mt: 0.5, fontSize: 14 }}>
                        {cargoTexto}
                    </Typography>
                    <div style={{
                        display: 'flex', alignItems: 'center',
                        gap: 6, marginTop: 4,
                        color: SLATE_500, fontSize: 13,
                    }}>
                        <BusinessIcon sx={{ fontSize: 16 }} />
                        <span>
                            {nombreDepartamento}
                            {negocioNombre ? ` · ${negocioNombre}` : ''}
                        </span>
                    </div>
                </div>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, color: SLATE_500, letterSpacing: 0.4 }}>
                    CONTACTO Y UBICACIÓN
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <EmailIcon fontSize="small" sx={{ color: BRAND }} />
                        {persona.mail_empresa ? (
                            <a href={`mailto:${persona.mail_empresa}`} style={{ color: BRAND }}>
                                {persona.mail_empresa}
                            </a>
                        ) : <span style={{ color: SLATE_500 }}>Sin email</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <PhoneIcon fontSize="small" sx={{ color: BRAND }} />
                        {persona.telefono_empresa ? (
                            <span>
                                {persona.telefono_empresa}
                                {persona.extension_telefono && (
                                    <span style={{ color: SLATE_500 }}> · ext. {persona.extension_telefono}</span>
                                )}
                            </span>
                        ) : <span style={{ color: SLATE_500 }}>Sin teléfono</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <LocationOnIcon fontSize="small" sx={{ color: BRAND }} />
                        {/* Población y provincia sólo llegan a los roles de la ficha (hotfix 23/10);
                            al resto le llega la oficina, y «Sin ubicación» diría más de lo que sabe. */}
                        {ubicacionLinea
                            ? <span>{ubicacionLinea}</span>
                            : <span style={{ color: SLATE_500 }}>{esStaff ? 'Sin ubicación' : 'Sin oficina'}</span>}
                    </div>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                {esStaff && (
                    <Button
                        startIcon={<LockIcon />}
                        onClick={onVerFicha}
                        variant="outlined"
                        sx={{ color: BRAND, borderColor: BRAND }}
                    >
                        Ver ficha completa
                    </Button>
                )}
                <Button
                    startIcon={<CenterFocusStrongIcon />}
                    onClick={onCenter}
                    variant="contained"
                    sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#012b49' } }}
                >
                    Centrar en organigrama
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Componente principal ───
function OrganigramaInner() {
    const navigate = useNavigate();
    const { persona } = useContext(UsuarioContext) || {};
    const rolStr = String(persona?.rol || '').toLowerCase();
    const esStaff = ['administrador', 'super administrador', 'recursos humanos'].includes(rolStr);

    const rf = useReactFlow();

    const [loading, setLoading] = useState(true);
    const [empresas, setEmpresas] = useState([]);
    const [empresaId, setEmpresaId] = useState(null);
    const [departamentos, setDepartamentos] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [asesorias, setAsesorias] = useState([]);

    // Posiciones manuales del lienzo (override del layout automático de dagre).
    // Mapa { [departamento_empresa_id]: { x, y } }. Lo que llega del backend
    // es el "guardado"; el "actual" se va actualizando al arrastrar y se
    // restaura al cancelar la edición.
    const [posicionesGuardadas, setPosicionesGuardadas] = useState({});
    const [posicionesActuales, setPosicionesActuales] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [savingPos, setSavingPos] = useState(false);
    const [editError, setEditError] = useState(null);
    const puedeEditar = ['administrador', 'super administrador'].includes(rolStr);

    const [modal, setModal] = useState(null);
    // modal: { persona, departamentoId } o null

    // Carga inicial: empresas + (de query string o primera).
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const empresaQuery = urlParams.get('empresa');

                const lista = await fetchNegociosData();
                if (!alive) return;
                const arr = Array.isArray(lista) ? lista : [];
                setEmpresas(arr);
                if (arr.length === 0) { setLoading(false); return; }
                const inicial = arr.find((e) => e.Id === Number(empresaQuery)) || arr[0];
                setEmpresaId(inicial.Id);
            } catch (err) {
                console.error('Organigrama: error cargando empresas', err);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Cuando cambia empresa: refetch departamentos + personas + asesorías.
    useEffect(() => {
        if (!empresaId) return;
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const nuevaUrl = `${window.location.pathname}?empresa=${empresaId}`;
                window.history.replaceState({ path: nuevaUrl }, '', nuevaUrl);

                /* El id de «Alta», BUSCADO POR NOMBRE. Se pide aqui y no dentro de
                 * `fetchPersonasFiltersData` a proposito: **el criterio de a quien pinta el
                 * organigrama tiene que verse en la pantalla que lo decide**, no escondido dentro
                 * de algo que parece una sola peticion.
                 *
                 * Si no se pudiera resolver, se manda `null` y el servidor no filtra por estado:
                 * se ve de mas, nunca de menos. En un organigrama faltar gente es peor que sobrar,
                 * porque un hueco no se nota y una fila de mas si. */
                const estados = await fetchEstadosData().catch(() => []);
                const idAlta = (estados || []).find(
                    (e) => String(e?.nombre || '').trim().toLowerCase() === 'alta')?.Id ?? null;

                const [depResp, persResp, asesResp, posResp] = await Promise.all([
                    fetchDepartamentosFiltersData(empresaId),
                    fetchPersonasFiltersData(empresaId, idAlta),
                    /* Estos dos SI se tragan el fallo a proposito, igual que `estados` arriba:
                     * son CAPAS OPCIONALES sobre el organigrama —asesorias y posiciones guardadas—
                     * y sin ellas el organigrama se pinta igual, solo que plano. Caerse entero
                     * porque no cargue una posicion guardada seria peor.
                     * Se escribe porque un `.catch(() => [])` sin explicacion se lee como descuido,
                     * y el 02/10 un barrido encontro cuatro: dos eran fallos y dos eran esto. */
                    fetchAsesoriasOrganigrama(empresaId).catch(() => []),
                    fetchPosicionesOrganigrama(empresaId).catch(() => ({})),
                ]);
                if (!alive) return;
                setDepartamentos(Array.isArray(depResp?.data) ? depResp.data : []);
                setPersonas(Array.isArray(persResp?.data) ? persResp.data : []);
                setAsesorias(Array.isArray(asesResp) ? asesResp : []);
                const pos = posResp && typeof posResp === 'object' ? posResp : {};
                setPosicionesGuardadas(pos);
                setPosicionesActuales(pos);
                setEditMode(false);
                setEditError(null);
            } catch (err) {
                console.error('Organigrama: error cargando datos del negocio', err);
                setDepartamentos([]); setPersonas([]); setAsesorias([]);
                setPosicionesGuardadas({}); setPosicionesActuales({});
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [empresaId]);

    const empresaActual = empresas.find((e) => e.Id === empresaId);
    const onOpenPersona = (p, departamentoId) => {
        setModal({
            persona: {
                ...p,
                _departamentoIdContexto: departamentoId,
                _negocioContexto: empresaActual?.nombre,
            },
            departamentoId,
        });
    };

    const { nodes: nodesBase, edges } = useMemo(() => buildGraph({
        negocio: empresaActual,
        departamentos,
        asesorias,
        personas,
        onOpenPersona,
    }), [empresaActual, departamentos, asesorias, personas]);

    // Nodos finales: cada nodo 'dept' cuyo data.departamentoId (clave) tenga
    // una posición guardada (por departamento_empresa_id) recibe esa
    // position por override. Los nodos sin posición guardada conservan la
    // calculada por dagre. Así el primer render usa dagre, y las posiciones
    // manuales se aplican encima.
    // Importante: para el override usamos la PK del DepartamentoEmpresa
    // (data.deId, que añadimos abajo en el buildGraph) — coincide con la
    // clave del map de posiciones del backend.
    const nodes = useMemo(() => {
        if (!nodesBase || nodesBase.length === 0) return nodesBase;
        const hayManuales = Object.keys(posicionesActuales).length > 0;
        if (!hayManuales) return nodesBase;
        return nodesBase.map((n) => {
            const deId = n?.data?.deId;
            if (deId == null) return n;
            const p = posicionesActuales[deId];
            if (!p) return n;
            return { ...n, position: { x: p.x, y: p.y } };
        });
    }, [nodesBase, posicionesActuales]);

    const handleChangeEmpresa = (event) => {
        setEmpresaId(Number(event.target.value));
    };

    // Captura la posición final de un nodo dept tras un drag y la almacena
    // en posicionesActuales. fitView NO se relanza al editar — sería molesto.
    const handleNodeDragStop = useCallback((_event, node) => {
        if (!editMode) return;
        const deId = node?.data?.deId;
        if (deId == null || !node.position) return;
        setPosicionesActuales((prev) => ({
            ...prev,
            [deId]: { x: node.position.x, y: node.position.y },
        }));
    }, [editMode]);

    const entrarEdicion = () => {
        setEditError(null);
        setPosicionesActuales(posicionesGuardadas);
        setEditMode(true);
    };
    const cancelarEdicion = () => {
        setEditError(null);
        setPosicionesActuales(posicionesGuardadas);
        setEditMode(false);
    };
    const guardarDisposicion = async () => {
        if (!empresaId) return;
        setSavingPos(true);
        setEditError(null);
        try {
            await savePosicionesOrganigrama(empresaId, posicionesActuales);
            setPosicionesGuardadas(posicionesActuales);
            setEditMode(false);
        } catch (err) {
            setEditError(err?.response?.data?.message || err?.message || 'Error guardando');
        } finally {
            setSavingPos(false);
        }
    };
    const restablecerAuto = async () => {
        if (!empresaId) return;
        // Volver al layout automático: borrar todo en backend y vaciar el
        // mapa local. Tras el save, los nodos vuelven a usar posiciones dagre.
        if (!window.confirm('Esto borrará las posiciones manuales del organigrama y volverá al layout automático. ¿Continuar?')) return;
        setSavingPos(true);
        setEditError(null);
        try {
            await resetPosicionesOrganigrama(empresaId);
            setPosicionesGuardadas({});
            setPosicionesActuales({});
            setEditMode(false);
        } catch (err) {
            setEditError(err?.response?.data?.message || err?.message || 'Error restableciendo');
        } finally {
            setSavingPos(false);
        }
    };

    const handleCenter = () => {
        if (!modal) return;
        // Busca el nodo del departamento del contexto.
        const target = nodes.find((n) =>
            n.type === 'dept' && n.data?.departamentoId === modal.departamentoId);
        if (target && rf?.setCenter) {
            const tw = target.data?.width || NODE_WIDTH;
            rf.setCenter(target.position.x + tw / 2,
                         target.position.y + 80,
                         { zoom: 1.0, duration: 500 });
        }
        setModal(null);
    };
    const handleVerFicha = () => {
        if (modal?.persona?.Id) {
            navigate(`/gestionpersonas/personas/editarPersona/${modal.persona.Id}`);
        }
        setModal(null);
    };

    return (
        <div style={{
            width: '100%', height: 'calc(100vh - 60px)',
            background: '#f8fafc', position: 'relative',
        }}>
            <Box sx={{ position: 'absolute', top: 12, left: 16, zIndex: 10 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                    <Select
                        value={empresaId || ''}
                        onChange={handleChangeEmpresa}
                        displayEmpty
                        sx={{ backgroundColor: 'rgba(255,255,255,0.95)', fontSize: 14 }}
                    >
                        {empresas.length === 0 && (
                            <MenuItem value="" disabled>Sin empresas</MenuItem>
                        )}
                        {empresas.map((emp) => (
                            <MenuItem key={emp.Id} value={emp.Id}>{emp.nombre}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {empresaActual && (
                    <div style={{
                        marginTop: 8, background: 'rgba(255,255,255,0.95)',
                        border: `1px solid ${SLATE_300}`, borderRadius: 6,
                        padding: '6px 10px', fontSize: 12, color: INK, maxWidth: 320,
                    }}>
                        <strong>{empresaActual.nombre}</strong> · {departamentos.length} departamentos · {personas.length} personas
                    </div>
                )}
            </Box>

            {/* Toolbar de edición — solo admin/super admin. En modo normal
                muestra "Editar disposición". En modo edición: Guardar,
                Cancelar y Restablecer. */}
            {puedeEditar && (
                <Box sx={{
                    position: 'absolute', top: 12, right: 16, zIndex: 10,
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1,
                }}>
                    {!editMode ? (
                        <button
                            type="button"
                            onClick={entrarEdicion}
                            style={btnToolbarStyle}
                        >
                            <Pencil size={14} />
                            Editar disposición
                        </button>
                    ) : (
                        <>
                            <div style={{
                                background: '#fff7ed', border: `1px solid #fed7aa`,
                                color: '#9a3412', borderRadius: 6,
                                padding: '6px 10px', fontSize: 12, maxWidth: 320,
                            }}>
                                Modo edición: arrastra las cajas y pulsa Guardar.
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                    type="button"
                                    onClick={guardarDisposicion}
                                    disabled={savingPos}
                                    style={{ ...btnToolbarStyle, ...btnPrimary }}
                                >
                                    <Save size={14} />
                                    {savingPos ? 'Guardando…' : 'Guardar disposición'}
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelarEdicion}
                                    disabled={savingPos}
                                    style={btnToolbarStyle}
                                >
                                    <XCloseIcon size={14} />
                                    Cancelar
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={restablecerAuto}
                                disabled={savingPos}
                                style={{ ...btnToolbarStyle, color: '#9a3412', borderColor: '#fed7aa' }}
                            >
                                <RotateCcw size={14} />
                                Restablecer a automático
                            </button>
                            {editError && (
                                <div style={{
                                    background: '#fef2f2', border: `1px solid #fecaca`,
                                    color: '#991b1b', borderRadius: 6,
                                    padding: '6px 10px', fontSize: 12, maxWidth: 320,
                                }}>
                                    {editError}
                                </div>
                            )}
                        </>
                    )}
                </Box>
            )}

            {loading ? (
                <div style={{
                    height: '100%', display: 'grid', placeItems: 'center', color: SLATE_500,
                }}>
                    Cargando organigrama…
                </div>
            ) : nodes.length === 0 ? (
                <div style={{
                    height: '100%', display: 'grid', placeItems: 'center', color: SLATE_500,
                }}>
                    No hay departamentos para mostrar.
                </div>
            ) : (
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    proOptions={{ hideAttribution: true }}
                    nodesDraggable={editMode}
                    onNodeDragStop={handleNodeDragStop}
                    panOnScroll
                >
                    <Background gap={16} color={SLATE_200} />
                    <Controls showInteractive={false} />
                    <MiniMap pannable zoomable nodeColor={() => BRAND} />
                </ReactFlow>
            )}

            <PersonaDialog
                open={!!modal}
                onClose={() => setModal(null)}
                persona={modal?.persona}
                departamentos={departamentos}
                onCenter={handleCenter}
                onVerFicha={handleVerFicha}
                esStaff={esStaff}
            />
        </div>
    );
}

export default function Organigrama() {
    // ReactFlowProvider habilita useReactFlow() dentro de OrganigramaInner
    // (lo usamos para hacer setCenter al pulsar "Centrar en organigrama").
    return (
        <ReactFlowProvider>
            <OrganigramaInner />
        </ReactFlowProvider>
    );
}
